import { createBigDecimal } from '@0xtorch/big-decimal'
import type { CryptoCurrency, Nft, Transfer } from '@0xtorch/core'
import type { z } from 'zod'
import type { Chain } from '../../../../chain'
import {
  type LowerHex,
  type TransactionDecoded,
  isHex,
  toLowerHex,
} from '../../../../types'
import { createEvmAddressId } from '../../../../utils'
import { getAllValue } from './allValue'
import { checkTransferIsMatchErc20Pattern } from './erc20Pattern'
import { checkTransferIsMatchErc721Pattern } from './erc721Pattern'
import { checkTransferIsMatchErc1155Pattern } from './erc1155Pattern'
import type { customTransferGeneratorSchema } from './schema'

export const generateCustomTransfer = ({
  schema,
  chain,
  transaction,
  relatedAddresses,
}: {
  readonly schema: z.infer<typeof customTransferGeneratorSchema>
  readonly chain: Chain
  readonly transaction: TransactionDecoded
  readonly relatedAddresses: Set<LowerHex>
}): Transfer => {
  const fromText =
    schema.from === undefined
      ? undefined
      : getAllValue({
          schema: schema.from,
          transaction,
          relatedAddresses,
        })
  const from =
    fromText === undefined || !isHex(fromText)
      ? undefined
      : createEvmAddressId({
          chainId: transaction.chainId,
          address: toLowerHex(fromText),
        })

  const toText =
    schema.to === undefined
      ? undefined
      : getAllValue({
          schema: schema.to,
          transaction,
          relatedAddresses,
        })
  const to =
    toText === undefined || !isHex(toText)
      ? undefined
      : createEvmAddressId({
          chainId: transaction.chainId,
          address: toLowerHex(toText),
        })

  const { asset, decimals } = getAssetWithDecimals({
    schema: schema.asset,
    chain,
    transaction,
    relatedAddresses,
  })

  const amountText = getAllValue({
    schema: schema.amount,
    transaction,
    relatedAddresses,
  })
  const amount = createBigDecimal(BigInt(amountText), decimals)

  return {
    direction: schema.direction,
    from,
    to,
    asset,
    amount,
  }
}

const getAssetWithDecimals = ({
  schema,
  chain,
  transaction,
  relatedAddresses,
}: {
  readonly schema: z.infer<typeof customTransferGeneratorSchema>['asset']
  readonly chain: Chain
  readonly transaction: TransactionDecoded
  readonly relatedAddresses: Set<LowerHex>
}): { readonly asset: CryptoCurrency | Nft; readonly decimals: number } => {
  switch (schema.type) {
    case 'native': {
      return {
        asset: chain.nativeToken.currency,
        decimals: chain.nativeToken.decimals,
      }
    }
    case 'erc20': {
      let currentIndex = -1
      for (const transfer of transaction.erc20Transfers) {
        const matched = checkTransferIsMatchErc20Pattern({
          transfer,
          schema,
          transaction,
          relatedAddresses,
        })
        if (matched) {
          currentIndex += 1
          if (currentIndex === schema.index) {
            return {
              asset: transfer.token.currency,
              decimals: transfer.token.decimals,
            }
          }
        }
      }
      throw new Error('Matched ERC20 transfer is not found')
    }
    case 'erc721': {
      let currentIndex = -1
      for (const transfer of transaction.erc721Transfers) {
        const matched = checkTransferIsMatchErc721Pattern({
          transfer,
          schema,
          transaction,
          relatedAddresses,
        })
        if (matched) {
          currentIndex += 1
          if (currentIndex === schema.index) {
            return {
              asset: transfer.token,
              decimals: 0,
            }
          }
        }
      }
      throw new Error('Matched ERC721 transfer is not found')
    }
    case 'erc1155': {
      let currentIndex = -1
      for (const transfer of transaction.erc1155Transfers) {
        const matched = checkTransferIsMatchErc1155Pattern({
          transfer,
          schema,
          transaction,
          relatedAddresses,
        })
        if (matched) {
          currentIndex += 1
          if (currentIndex === schema.index) {
            return {
              asset: transfer.token,
              decimals: 0,
            }
          }
        }
      }
      throw new Error('Matched ERC1155 transfer is not found')
    }
    default: {
      throw new Error(`Unknown asset type: ${schema satisfies never}`)
    }
  }
}
